import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, matchPath, useLocation } from 'react-router-dom';
import type { FormApi } from 'final-form';
import dayjs from 'dayjs';
import * as queryString from 'query-string';
import cookie from 'js-cookie';
import { t } from 'ttag';
import debounce from 'lodash/debounce';

import {
  analiticAppliedFiltersKey,
  BRAND_PAGE_REFERRER,
  brandReferrerKey,
  CANARY_COOKIE,
  CSRF_TOKEN_HEADER_NAME,
  emailRegexp,
  navProfileKey,
  notAuthUsersEmailKey,
  openedTopProductIdKey,
  PRODUCT_PAGE_REFERRER,
  productCollectionSlugKey,
  productReferrerKey,
  productReferrerPathnameKey,
} from '@constants';
import {
  productDetails,
  baseCatalogBrand,
  baseCatalogCity,
  catalogBrand,
  catalogCity,
} from '@legacyUrls';
import {
  baseCatalogIndexedFilter,
  catalogIndexedFilter,
  logout as logoutLink,
  collections,
  looksCollection,
  newArrivals,
  dayDiscount,
  baseCatalog,
  catalog,
  login,
} from '@urls';
import { DeliveryService } from '@v5Models';
import { FeatureFlagsQuery } from '@hook/useFlags/gql/graphql.v5.generated';
import { Region } from '@hook/useRegion';
import { ActiveFilters } from '@hook/productFilters';

export const getIsCanary = () => !!cookie.get(CANARY_COOKIE);

export const getCSRFToken = () => cookie.get('csrftoken') ?? '';

export const getShafaAbsoluteUrl = (pathname: string) => {
  const safePathname = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return `${SHAFA_DOMAIN}${safePathname}`;
};

export const getOutletAbsoluteUrl = (pathname: string) => {
  const safePathname = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return `${OUTLET_DOMAIN}${safePathname}`;
};

export const getIsOutletByHost = (host: string) => {
  return OUTLET_DOMAIN.includes(host);
};

export const getProductModerationAdminPage = (productId: number) => (
  `/moderation/filter?product_id=${productId}`
);

/**
 *  Convert 1000 to 1 000
*/
export const prettierNumber = (num: number | string): string => (
  Number(num.toString().split('.')[0]).toLocaleString().replace(/,/g, ' ')
);

export const prettierTtn = (ttn: string, deliveryService: DeliveryService): string => {
  if (deliveryService === DeliveryService.Justin) {
    const groupSize = 4;
    const groupsCount = Math.round(ttn.length / groupSize);
    const groups = [];
    for (let i = 0; i < groupsCount; i += 1) {
      groups.push(ttn.slice(i * groupSize, (i + 1) * groupSize));
    }

    const tail = ttn.slice(groupsCount * groupSize);
    if (tail) {
      groups.push(tail);
    }

    return groups.join(' ');
  }

  return ttn;
};

export const getFormattedCreatedAt = (dateString: string) => {
  // Return date in format dd.mm.yyyy
  const date = new Date(dateString.replace(/\s/, 'T'));

  let month = String(date.getMonth() + 1);
  month = month.length === 1 ? `0${month}` : month;

  let day = String(date.getDate());
  day = day.length === 1 ? `0${day}` : day;

  return `${day}.${month}.${date.getFullYear()}`;
};

export const getFormattedDayMonth = (dateString: string) => (
  getFormattedCreatedAt(dateString).slice(0, 5)
);

export const getErrorsResponseMapping = (errors: GraphFieldErrorList[]) => (
  errors.reduce<ErrorsMapping>(
    (acc, fieldError) => ({ ...acc, [fieldError.field]: fieldError.messages }),
    {},
  )
);

export const getFieldsSingleError = (errors: GraphFieldErrorList[]): SingleErrorMapping => (
  errors.reduce(
    (acc, { field, messages }) => ({ ...acc, [field]: messages[0].message }),
    {},
  )
);

export const getFieldsMultiError = (errors: GraphFieldErrorList[]): MultiErrorMapping => (
  errors.reduce(
    (acc, { field, messages }) => ({
      ...acc,
      [field]: messages.map(({ message }) => message),
    }),
    {},
  )
);

export const isPhoneNumberValid = (phoneNumber: string, region: Region): boolean => {
  const phoneWithoutPlus = (
    phoneNumber.startsWith('+')
      ? phoneNumber.slice(1)
      : phoneNumber
  );
  return (
    Boolean(
      +phoneWithoutPlus
      && new RegExp(region.phoneCheckRegexp).exec(phoneWithoutPlus),
    )
  );
};

export const isEmailValid = (email: string): boolean => (
  emailRegexp.test(email)
);

export const scrollToTop = (scrollDuration: number) => {
  const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
  let start: number;

  function animateScroll(time: number) {
    if (!start) {
      start = time;
    }
    let timeFraction = (time - start) / scrollDuration;
    if (timeFraction > 1) {
      timeFraction = 1;
    }

    window.scrollTo(
      0,
      currentScroll - currentScroll * timeFraction,
    );

    if (timeFraction < 1) {
      requestAnimationFrame(animateScroll);
    }
  }

  requestAnimationFrame(animateScroll);
};

export const staticfile = (path: string) => (
  STATIC_URL + encodeURIComponent(path)
);

export const logout = () => {
  try {
    sessionStorage.removeItem(navProfileKey);
  } catch (e) {
    console.log(e);
  }

  return fetch(
    logoutLink,
    {
      method: 'POST',
      headers: {
        [CSRF_TOKEN_HEADER_NAME]: getCSRFToken(),
      },
    },
  );
};

export const setEmailNotAuthUser = (email: string) => {
  sessionStorage.setItem(notAuthUsersEmailKey, email);
};

export const getEmailNotAuthUser = () => sessionStorage.getItem(notAuthUsersEmailKey);

export const minNumber = (a: number, b: number) => (
  a < b ? a : b
);

export const enumerate = (count: number, dec: string[]) => {
  let num = count;

  if (num > 100) {
    num %= 100;
  }
  if (num <= 20 && num >= 10) {
    return dec[2];
  }
  if (num > 20) {
    num %= 10;
  }

  return num === 1 ? dec[0] : num > 1 && num < 5 ? dec[1] : dec[2];
};

export enum MobileOS {
  ANDROID = 'ANDROID',
  IOS = 'IOS',
  OTHER = 'OTHER',
}

const MobileOSToUserAgentRegex = {
  [MobileOS.ANDROID]: [/Android/i],
  [MobileOS.IOS]: [
    /iPhone/i,
    /iPad/i,
    /iPod/i,
  ],
  [MobileOS.OTHER]: [
    /webOS/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ],
};

/**
 * Detect mobile OS based on user agent
 */
export const getMobileOS = () => {
  const foundMobileOS = Object.entries(MobileOSToUserAgentRegex).find(
    ([, regex]) => regex.some((toMatchItem) => toMatchItem.exec(navigator.userAgent)),
  );

  return foundMobileOS?.[0];
};

type CatalogRouteMatchData = { namespace: string, indexedFilter?: string } & (
  { brand: string }
  | { city: string }
  | { slug: string }
  | { slug: string, brand: string }
  | { slug: string, city: string }
  | { category?: string }
);

export const matchCatalogPath = (path: string) => {
  const safePath = path.startsWith('/') ? path : `/${path}`;

  return matchPath<CatalogRouteMatchData>(safePath, [
    // Order matters, `catalog` and `baseCatalog` have to be last, because city and brand routes
    // maybe matched as catalog slug.
    productDetails,
    catalogBrand,
    catalogCity,
    baseCatalogBrand,
    baseCatalogCity,
    catalogIndexedFilter,
    baseCatalogIndexedFilter,
    catalog,
    baseCatalog,
  ]);
};

export const useCatalogRouteMatch = () => {
  const location = useLocation();
  const match = matchCatalogPath(location.pathname);

  return match;
};

interface CollectionMatchPathData {
  slug: string,
}

export const matchCollectionPath = (path: string) => {
  const safePath = path.startsWith('/') ? path : `/${path}`;

  const collectionMatch = matchPath<CollectionMatchPathData>(safePath, [
    collections,
    looksCollection,
    newArrivals,
    dayDiscount,
  ]);

  if (collectionMatch?.path === newArrivals) {
    return {
      ...collectionMatch,
      params: {
        ...collectionMatch.params,
        slug: newArrivals.slice(1),
      },
    };
  }

  if (collectionMatch?.path === dayDiscount) {
    return {
      ...collectionMatch,
      params: {
        ...collectionMatch.params,
        slug: dayDiscount.slice(1),
      },
    };
  }

  return collectionMatch;
};

export const useCollectionRouteMatch = () => {
  const location = useLocation();
  const match = matchCollectionPath(location.pathname);

  return match;
};

export const useIsClient = () => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => setIsClient(true), []);

  return isClient;
};

/*
useSearchParams - is used to work with search params as with regular state objects.
Return:
  - searchParams: parsed search object
  - replaceSearchParams: function that gets object with new params and set them
    in search string without touching another search params
  - setSearchParams: function that gets object with new params and set them
    in search string, all other params that not listed on object will be removed
    from search string.

Generic param `T` is used to tell typescript what object we're going to save and work with.
Using this generic param, hook returns objects with the same optional keys, but values
are always strings or array of strings.
*/
type SearchParamValue = string | number | boolean | undefined;
type SearchObject<T extends Record<string, SearchParamValue | SearchParamValue[]>> = {
  [K in keyof T]?: T[K]
};
export function useSearchParams<T extends Record<string, SearchParamValue | SearchParamValue[]>>() {
  const searchParamsRef = useRef<SearchObject<T>>(null);
  const history = useHistory();
  const { location } = history;

  const searchParams = useMemo<SearchObject<T>>(

    () => queryString.parse(location.search),
    [location.search],
  );
  // We need ref to be able to get current search params in `replaceSearchParams`
  // even if this function memoized somewhere. It `replaceSearchParams` memoized
  // then it might be closed on old search params.
  searchParamsRef.current = searchParams;

  const replaceSearchParams = useCallback(
    (params: Partial<T>, replaceHistory = false) => {
      const newSearchParams = queryString.stringify({ ...searchParamsRef.current, ...params });
      if (replaceHistory) {
        history.replace({ ...location, search: newSearchParams });
      } else {
        history.push({ ...location, search: newSearchParams });
      }
    },
    [history, location],
  );

  const setSearchParams = useCallback(
    (params: Partial<T>, replaceHistory = false) => {
      const newSearchParams = queryString.stringify(params);
      if (replaceHistory) {
        history.replace({ ...location, search: newSearchParams });
      } else {
        history.push({ ...location, search: newSearchParams });
      }
    },
    [history, location],
  );

  return [searchParams, replaceSearchParams, setSearchParams] as const;
}

export const containSaleConfirmationRequiredError = (errors: GraphFieldErrorList[]) => (
  errors.some(
    (error) => error.field === '__all__'
    && error.messages.some(
      (message) => message.code === 'sale_confirmation_required',
    ),
  )
);

export const disableWindowScroll = () => {
  document.body.style.top = `-${window.scrollY}px`;
  document.body.style.position = 'fixed';
};

export const enableWindowScroll = () => {
  const scrollY = document.body.style.top;
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
};

const scrollToFirstErrorScrollOptions: ScrollIntoViewOptions = {
  block: 'center',
  behavior: 'smooth',
};

export const scrollToFirstError = (form: FormApi) => {
  const { errors, submitErrors } = form.getState();
  const fieldsNames = Object.keys({
    ...errors,
    ...submitErrors,
  });

  // Get top input (nearest to top)
  const topInput = fieldsNames.reduce(
    (acc, name) => {
      // If input exists and placed higher than last found input
      // with error, then remember it
      const input = document.querySelector(`[name="${name}"]`);
      if (input) {
        const { top } = input.getBoundingClientRect();

        if (!acc || top < acc[1]) {
          return [input, top] as [HTMLInputElement, number];
        }
      }

      return acc;
    },
    undefined as [HTMLInputElement, number] | undefined,
  );

  if (topInput) {
    const [input] = topInput;

    // Custom radio inputs hidden, so we can't just scroll to them,
    // in such case we scroll to parent element
    const { x, y } = input.getBoundingClientRect();
    if (!x && !y && input.parentElement) {
      input.parentElement.scrollIntoView(scrollToFirstErrorScrollOptions);
    } else {
      input.scrollIntoView(scrollToFirstErrorScrollOptions);
    }
  }
};

export const formatPrecisePrice = (price: number) => (
  Number.isInteger(price)
    ? price
    : price.toFixed(2)
);

export const convertLastVisitToText = (lastVisit: dayjs.Dayjs) => {
  const now = dayjs();
  const days = now.diff(lastVisit, 'day');
  const hours = now.diff(lastVisit, 'hour');
  const minutes = now.diff(lastVisit, 'minute');

  if (days > 30) return t`30+ дн.`;
  if (days > 0) return t`${days} дн.`;
  if (hours > 0) return t`${hours} год.`;
  if (minutes >= 5) return t`${minutes} хв.`;
  return t`В мережі`;
};

const ADVERT_GET_PARAMETERS = new Set([
  'gclid',
  'utm_medium',
  'utm_source',
  'utm_campaign',
  'utm_content',
  '_openstat',
]);
export const hasAdvertisementParameter = (params: string) => {
  const parsedParams = queryString.parse(params);
  return !!Object
    .keys(parsedParams)
    .find((param) => ADVERT_GET_PARAMETERS.has(param));
};

interface CharacteristicFilter {
  id: number,
  choices: {
    id: number,
  }[],
}

export const prepareAppliedFiltersForAnalytics = ({
  activeFilters,
  characteristics = [],
}: {
  activeFilters: ActiveFilters,
  characteristics?: CharacteristicFilter[],
}) => {
  const result = [];

  if (activeFilters.brands.length) {
    result.push('brand');
  }

  if (activeFilters.characteristics.length) {
    const characteristicGroups = characteristics.filter(
      (group) => group.choices.find(
        (choice) => activeFilters.characteristics.includes(choice.id),
      ),
    );

    characteristicGroups.forEach((group) => result.push(group.id));
  }

  if (activeFilters.cities.length) {
    result.push('city');
  }

  if (activeFilters.colors.length) {
    result.push('color');
  }

  if (activeFilters.conditions.length) {
    result.push('condition');
  }

  if (activeFilters.freeShipping) {
    result.push('free_shipping');
  }

  if (activeFilters.isOnSale) {
    result.push('sale');
  }

  if (activeFilters.priceFrom || activeFilters.priceTo || activeFilters.prices.length) {
    result.push('price');
  }

  if (activeFilters.sizes.length) {
    result.push('size');
  }

  return result.join('; ');
};

export const setAnaliticAppliedFiltersToStorage = ({
  pathname,
  filters,
}: {
  pathname: string,
  filters: string,
}) => {
  const prevValue = sessionStorage.getItem(analiticAppliedFiltersKey) || '{}';

  sessionStorage.setItem(analiticAppliedFiltersKey, JSON.stringify({
    ...JSON.parse(prevValue),
    [pathname]: filters,
  }));
};

export const getAnaliticAppliedFiltersFromStorage = (pathname: string) => {
  const value = sessionStorage.getItem(analiticAppliedFiltersKey) || '{}';
  return JSON.parse(value)[pathname] as (string) | undefined;
};

export const setProductReferrerPathnameToStorage = ({
  productId,
  pathname,
}: {
  productId: number,
  pathname: string,
}) => {
  const prevValue = sessionStorage.getItem(productReferrerPathnameKey) || '{}';

  sessionStorage.setItem(productReferrerPathnameKey, JSON.stringify({
    ...JSON.parse(prevValue),
    [productId]: pathname,
  }));
};

export const getProductReferrerPathnameFromStorage = (productId: number) => {
  const value = sessionStorage.getItem(productReferrerPathnameKey) || '{}';
  return JSON.parse(value)[productId] as (string) | undefined;
};

export const setProductCollectionSlug = (productId: number, slug: string) => {
  try {
    const prevValue = sessionStorage.getItem(productCollectionSlugKey) || '{}';

    sessionStorage.setItem(productCollectionSlugKey, JSON.stringify({
      ...JSON.parse(prevValue),
      [productId]: slug,
    }));
  } catch (err) {

    console.error(err);
  }
};

export const getProductCollectionSlug = (productId: number) => {
  let collectionSlug: string | undefined;

  try {
    const value = sessionStorage.getItem(productCollectionSlugKey) || '{}';

    collectionSlug = JSON.parse(value)[productId] || '';
  } catch (err) {

    console.error(err);
  }

  return collectionSlug;
};

export const getStringifiedKey = (key: string | number) => `key${key}`;


export function stringifyKeys<T>(values: Record<number | string, T>): Record<string, T>  {
  return Object
    .keys(values)
    .reduce(
      (result, key) => {

        result[getStringifiedKey(key)] = values[key];
        return result;
      },

      {} as Record<string, T>,
    );
}


export function destringifyKeys<T>(values: Record<string, T>): Record<number, T> {
  return Object
    .keys(values)
    .reduce(
      (result, key) => {

        result[Number(key.substring(3))] = values[key];
        return result;
      },

      {} as Record<number, T>,
    );
}

export const redirectToLogin = () => {
  const searchParams = new URLSearchParams({ next: location.href });

  if (!location.pathname.startsWith(login)) {
    location.href = `${login}?${searchParams.toString()}`;
  }
};

// must be sync with "get_test_flags_string" on backend shafa/jinja2.py
export const getTestFlagsString = (flags: FeatureFlagsQuery['settings']['featureFlags']) => {
  const result = [];

  if (flags.staticABTestFlag) {
    result.push('static_a');
  } else {
    result.push('static_b');
  }

  if (flags.SHAF19375ScoreFormulaTest) {
    result.push('ranking_formula_5_b');
  } else {
    result.push('ranking_formula_5_a');
  }

  if (flags.SHAF20305ScoreFormula7Test) {
    result.push('ranking_formula_7_b');
  } else {
    result.push('ranking_formula_7_a');
  }

  if (flags.SHAF20467ScoreFormula8Test) {
    result.push('ranking_formula_8_b');
  } else {
    result.push('ranking_formula_8_a');
  }

  if (flags.SHAF20611ScoreFormula10Test) {
    result.push('ranking_formula_10_b');
  } else {
    result.push('ranking_formula_10_a');
  }

  if (flags.SHAF19305NewBuyerAb) {
    result.push('new_buyer_a');
  } else {
    result.push('new_buyer_b');
  }
  if (flags.SHAF19654KviAb) {
    result.push('kvi_b');
  } else {
    result.push('kvi_a');
  }

  return result.join(', ');
};

// source https://github.com/lodash/lodash/issues/4815#issuecomment-1006573679
export function asyncDebounce<F extends(...args: unknown[]) => Promise<unknown>>(func: F, wait?: number) {
  const resolveSet = new Set<(p: unknown)=>void>();
  const rejectSet = new Set<(p: unknown)=>void>();

  const debounced = debounce((args: Parameters<F>) => {
    func(...args)
      .then((...res) => {
        resolveSet.forEach((resolve) => resolve(...res));
        resolveSet.clear();
      })
      .catch((...res) => {
        rejectSet.forEach((reject) => reject(...res));
        rejectSet.clear();
      });
  }, wait);

  return (...args: Parameters<F>): ReturnType<F> => new Promise((resolve, reject) => {
    resolveSet.add(resolve);
    rejectSet.add(reject);
    debounced(args);
  }) as ReturnType<F>;
}

export const setProductPageReferrerToStorage = (
  productId: number,
  referrer: typeof PRODUCT_PAGE_REFERRER[keyof typeof PRODUCT_PAGE_REFERRER],
) => {
  const prevValue = sessionStorage.getItem(productReferrerKey) || '{}';

  sessionStorage.setItem(productReferrerKey, JSON.stringify({
    ...JSON.parse(prevValue),
    [productId]: referrer,
  }));
};

export const getProductPageReferrerFromStorage = (productId: number) => {
  const value = sessionStorage.getItem(productReferrerKey) || '{}';
  return JSON.parse(value)[productId] as (typeof PRODUCT_PAGE_REFERRER[keyof typeof PRODUCT_PAGE_REFERRER]) | undefined;
};

export const setBrandPageReferrerToStorage = (
  brandName: string,
  referrer: typeof BRAND_PAGE_REFERRER[keyof typeof BRAND_PAGE_REFERRER],
) => {
  const prevValue = sessionStorage.getItem(brandReferrerKey) || '{}';

  sessionStorage.setItem(brandReferrerKey, JSON.stringify({
    ...JSON.parse(prevValue),
    [brandName]: referrer,
  }));
};

export const getBrandPageReferrerFromStorage = (brandName: string) => {
  const value = sessionStorage.getItem(brandReferrerKey) || '{}';
  return JSON.parse(value)[brandName] as (typeof BRAND_PAGE_REFERRER[keyof typeof BRAND_PAGE_REFERRER]) | undefined;
};

export const setOpenedTopToStorage = (topProductId: number) => {
  sessionStorage.setItem(openedTopProductIdKey, String(topProductId));
};

export const getOpenedTopFromStorage = () => {
  const topProductId = sessionStorage.getItem(openedTopProductIdKey);
  return topProductId ? Number(topProductId) : undefined;
};

export const createHTMLForm = (url: string, params: Record<string, string>) => {
  const form = document.createElement('form');
  const submit = document.createElement('button');

  form.setAttribute('method', 'post');
  form.setAttribute('action', url);
  form.style.visibility = 'hidden';

  Object.entries(params).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.setAttribute('name', name);
    input.setAttribute('value', value);
    form.appendChild(input);
  });

  submit.setAttribute('type', 'submit');
  form.appendChild(submit);

  document.body.appendChild(form);
  return form;
};

export const getAnalyticConfigs = (isOutlet: boolean) => {
  let gtagId;
  let googleAdsIds;
  let esputnikId;
  let facebookPixelId;
  let rtbId;
  let clerkEndpointUrl;
  let clerkMirrorDimensions;

  if (!__DEV__) {
    gtagId = isOutlet ? OUTLET_GTAG_ID : GTAG_ID;
    googleAdsIds = isOutlet ? OUTLET_GOOGLE_ADS_IDS : GOOGLE_ADS_IDS;
    esputnikId = isOutlet ? undefined : SITE_ESPUTNIK_ID;
    facebookPixelId = isOutlet ? undefined : SITE_FACEBOOK_TRACKING_ID;
    rtbId = isOutlet ? undefined : SITE_RTB_TRACKING_ID;
    clerkEndpointUrl = isOutlet ? undefined : CLERK_ENDPOINT;
    clerkMirrorDimensions = isOutlet ? undefined : CLERK_MIRROR_DIMENSIONS;
  }

  return {
    gtagId,
    googleAdsIds,
    esputnikId,
    facebookPixelId,
    rtbId,
    clerkEndpointUrl,
    clerkMirrorDimensions,
  };
};
